import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { PageHeader, SectionCard } from '@striker/ui-shell';
import { apiFetch } from '../auth/session';

type CategoryOpt = { id: number; code: string; name: string };

export function MasterNewTenantPage() {
  const navigate = useNavigate();
  const [categories, setCategories] = useState<CategoryOpt[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({
    business_name: '',
    slug: '',
    category_code: 'restaurant',
    owner_email: '',
    owner_password: '',
    owner_display_name: '',
    trial_days: '14',
  });

  useEffect(() => {
    void (async () => {
      try {
        const catsRes = await apiFetch('/api/v1/public/categories');
        const catsBody = (await catsRes.json().catch(() => ({}))) as { categories?: CategoryOpt[] };
        if (catsRes.ok && Array.isArray(catsBody.categories)) {
          setCategories(catsBody.categories);
        }
      } catch {
        /* ignore */
      }
    })();
  }, []);

  function patch(key: keyof typeof form, value: string) {
    setForm((prev) => ({ ...prev, [key]: value }));
    setError(null);
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await apiFetch('/api/v1/master/tenants', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          business_name: form.business_name.trim(),
          slug: form.slug.trim() || undefined,
          plan_code: 'free',
          category_code: form.category_code,
          owner_email: form.owner_email.trim(),
          owner_password: form.owner_password,
          owner_display_name: form.owner_display_name.trim() || undefined,
          trial_days: Number(form.trial_days) || 14,
        }),
      });
      const body = (await res.json().catch(() => ({}))) as {
        tenant?: { id: number; slug: string };
        message?: string;
        error?: string;
      };
      if (!res.ok) throw new Error(body.message || body.error || `HTTP ${res.status}`);
      navigate('/master/tenants', {
        replace: true,
        state: { created: body.tenant?.slug || body.tenant?.id },
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setBusy(false);
    }
  }

  return (
    <section className="sk-mc-page">
      <PageHeader
        title="Nuovo tenant"
        subtitle={
          <>
            <Link to="/master/tenants">← Tenant</Link> · store + owner + billing TRIAL
          </>
        }
      />

      {error ? <p className="sk-error">{error}</p> : null}

      <SectionCard>
        <form className="sk-saas-form" onSubmit={(e) => void onSubmit(e)}>
          <label className="sk-field">
            <span>Ragione sociale</span>
            <input
              className="sk-input"
              required
              value={form.business_name}
              onChange={(e) => patch('business_name', e.target.value)}
            />
          </label>
          <label className="sk-field">
            <span>Slug (opzionale)</span>
            <input
              className="sk-input"
              value={form.slug}
              placeholder="auto da nome"
              onChange={(e) => patch('slug', e.target.value)}
              spellCheck={false}
            />
          </label>
          <label className="sk-field">
            <span>Categoria</span>
            <select
              className="sk-input"
              value={form.category_code}
              onChange={(e) => patch('category_code', e.target.value)}
            >
              {(categories.length
                ? categories
                : [
                    { id: 1, code: 'restaurant', name: 'Ristorante' },
                    { id: 2, code: 'beauty', name: 'Centro Estetico' },
                    { id: 3, code: 'gym', name: 'Palestra' },
                  ]
              ).map((c) => (
                <option key={c.code} value={c.code}>
                  {c.name}
                </option>
              ))}
            </select>
          </label>
          <label className="sk-field">
            <span>Trial (giorni)</span>
            <input
              className="sk-input"
              type="number"
              min={1}
              max={90}
              value={form.trial_days}
              onChange={(e) => patch('trial_days', e.target.value)}
            />
          </label>
          <label className="sk-field">
            <span>Owner email</span>
            <input
              className="sk-input"
              type="email"
              required
              value={form.owner_email}
              onChange={(e) => patch('owner_email', e.target.value)}
              autoComplete="off"
            />
          </label>
          <label className="sk-field">
            <span>Owner password</span>
            <input
              className="sk-input"
              type="password"
              required
              minLength={8}
              value={form.owner_password}
              onChange={(e) => patch('owner_password', e.target.value)}
              autoComplete="new-password"
            />
          </label>
          <label className="sk-field">
            <span>Display name</span>
            <input
              className="sk-input"
              value={form.owner_display_name}
              onChange={(e) => patch('owner_display_name', e.target.value)}
            />
          </label>
          <button type="submit" className="sk-btn sk-btn--block" disabled={busy}>
            {busy ? 'Creazione…' : 'Crea tenant (Free)'}
          </button>
        </form>
      </SectionCard>
    </section>
  );
}
